import { useState, useEffect, useCallback, useMemo } from 'react'
import { useDataCache } from '@/contexts/DataCacheContext'
import {
  fetchSalesWithDateRange,
  fetchPriceLists,
  fetchCustomerBranches,
  createSale,
  createSaleItems,
  updateSale,
  deleteSale,
  deleteSaleItemsBySaleId,
  multiplierToDiscountPercent
} from '../supabase-client'
import { toLocalDateString } from '../utils/dateUtils'


const DEFAULT_STATUSES = ['pending', 'confirmed', 'delivered', 'invoiced', 'paid']

/**
 * useQuickOrders — data hook for Quick Order interface.
 *
 * @param {Object} config
 * @param {string|null} config.customerId — lock to one customer (portal) or null for all (admin)
 * @param {string[]} config.allowedStatuses — statuses visible in the interface
 */
export default function useQuickOrders({
  customerId = null,
  allowedStatuses = DEFAULT_STATUSES
} = {}) {
  const { getCustomers, getProducts, getProductTypes } = useDataCache()

  // Reference data
  const [customers, setCustomers] = useState([])
  const [products, setProducts] = useState([])
  const [productTypes, setProductTypes] = useState([])

  // Orders
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filter state
  const [filter, setFilter] = useState({ status: 'actionable', search: '' })

  // Load reference data once
  useEffect(() => {
    const load = async () => {
      try {
        const [c, p, pt] = await Promise.all([
          getCustomers(),
          getProducts(),
          getProductTypes()
        ])
        setCustomers(c || [])
        setProducts(p || [])
        setProductTypes(pt || [])
      } catch (err) {
        console.error('[useQuickOrders] Error loading reference data:', err)
        setError(err)
      }
    }
    load()
  }, [getCustomers, getProducts, getProductTypes])

  // Fetch orders — 3-week window (7 days back, 14 days forward)
  const fetchOrders = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const today = new Date()
      const startDate = new Date(today)
      startDate.setDate(today.getDate() - 7)
      const endDate = new Date(today)
      endDate.setDate(today.getDate() + 14)

      const start = toLocalDateString(startDate)
      const end = toLocalDateString(endDate)

      let sales = await fetchSalesWithDateRange(start, end)

      // Filter by customerId if locked
      if (customerId) {
        sales = sales.filter(s => s.customer_id === customerId)
      }

      // Filter to allowed statuses
      sales = sales.filter(s => allowedStatuses.includes(s.status))

      setOrders(sales)
    } catch (err) {
      console.error('[useQuickOrders] Error fetching orders:', err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [customerId, allowedStatuses])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  // Filtered orders based on current filter
  const filteredOrders = useMemo(() => {
    let result = orders

    if (filter.status === 'actionable') {
      result = result.filter(o => o.status === 'pending' || o.status === 'confirmed')
    } else if (filter.status !== 'all') {
      result = result.filter(o => o.status === filter.status)
    }

    if (filter.search) {
      const q = filter.search.toLowerCase()
      result = result.filter(o =>
        o.customer?.name?.toLowerCase().includes(q)
      )
    }

    // Sort by date descending (most recent first)
    result.sort((a, b) => new Date(b.date) - new Date(a.date))

    return result
  }, [orders, filter])

  // CRUD operations
  const createOrder = useCallback(async (saleData, items) => {
    const savedSale = await createSale(saleData)
    if (items && items.length > 0) {
      const saleItemsData = items.map(item => ({
        sale_id: savedSale.id,
        product_id: item.product_id,
        product_type_id: item.product_type_id,
        quantity: item.quantity,
        price: item.price,
        discount_percent: item.discount_percent || 0,
        discount_note: item.discount_note || null
      }))
      await createSaleItems(saleItemsData)
    }
    await fetchOrders()
    return savedSale
  }, [fetchOrders])

  const updateOrder = useCallback(async (saleId, saleData, items) => {
    await updateSale(saleId, saleData)
    await deleteSaleItemsBySaleId(saleId)
    if (items && items.length > 0) {
      const saleItemsData = items.map(item => ({
        sale_id: saleId,
        product_id: item.product_id,
        product_type_id: item.product_type_id,
        quantity: item.quantity,
        price: item.price,
        discount_percent: item.discount_percent || 0,
        discount_note: item.discount_note || null
      }))
      await createSaleItems(saleItemsData)
    }
    await fetchOrders()
  }, [fetchOrders])

  const deleteOrder = useCallback(async (saleId) => {
    await deleteSale(saleId)
    await fetchOrders()
  }, [fetchOrders])

  // Price list helpers
  const loadPriceLists = useCallback(async (customerType) => {
    if (!customerType) return []
    return fetchPriceLists(customerType.toLowerCase())
  }, [])

  const loadBranches = useCallback(async (custId) => {
    if (!custId) return []
    return fetchCustomerBranches(custId)
  }, [])

  return {
    // Reference data
    customers,
    products,
    productTypes,
    // Orders
    orders: filteredOrders,
    allOrders: orders,
    loading,
    error,
    // Filter
    filter,
    setFilter,
    // CRUD
    createOrder,
    updateOrder,
    deleteOrder,
    // Helpers
    loadPriceLists,
    loadBranches,
    refreshOrders: fetchOrders,
    multiplierToDiscountPercent
  }
}
